import isElectron from 'is-electron';
import { batch } from 'react-redux';
import {
  updateProfileImage,
  fetchSsoUrl,
  ssoLogin as ssoLoginAPI,
} from '../../../apis/authApi/authAPI';
import { tryUpdatePushToken } from '../../Sidebar/actionCreators';
import { legacyLoginRequest } from '../../../api/legacySessions';
import { getUserById, getUserPermissions, updateUser } from '../../../api/users';
import { selectTenant, selectTenantId } from '../../Organizations/OrganizationsSlice';
import type { AppThunk, RootState } from '../../../store';
import { removeItem, getItem } from '../../../utils/storage';
import { getSsoRedirectUrl } from '../helpers';
import { fetchTenant } from '../../Organizations/OrganizationsSlice/actionCreators';
import {
  selectUser,
  selectUserId,
  setIsLoginError,
  setIsLoginLoading,
  setIsUserVerified,
  setUser,
  setUserPermissions,
} from './index';
import {
  convertLegacyAccountInfoToCore,
  convertLegacyUserByIdToCore,
  getLegacyAccountInfo,
} from '../../../api/legacyUsers';
import { setEmail, setPassword, setLegacy2faData } from '../../Login2fa/Login2faSlice';
import { shouldUseCoreApi } from '../../../utils/features';

type LoginProps = {
  email: string;
  password: string;
};

export type SsoLoginProps = {
  email: string | null;
  code: string;
  provider?: string;
};

type ErrorWithCode = {
  Code: string;
};

class NoUserIdError extends Error {}
class MissingTenantError extends Error {}

const extractErrorMessageFromResponse = (error: ErrorWithCode | unknown) => {
  // eslint-disable-next-line no-prototype-builtins
  const errorCode = error && error.hasOwnProperty('Code') && (error as ErrorWithCode).Code;
  if (!errorCode) return;

  if (errorCode == 'INCORRECT_PASSWORD') {
    return 'incorrect_password';
  } else if (errorCode == 'TOO_MANY_ATTEMPTS') {
    return 'login_error_many_attempts';
  }

  return 'login_error';
};

export const login =
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  (props: LoginProps): AppThunk<Promise<'2fa' | void>> =>
    async dispatch => {
      const { email, password } = props;
      try {
        dispatch(setIsLoginLoading(true));
        const legacyLoginResponse = await legacyLoginRequest({
          email,
          password,
        });

        if ('TwoFactorAuthCodeRequired' in legacyLoginResponse) {
          batch(() => {
            dispatch(setLegacy2faData(legacyLoginResponse));
            dispatch(setEmail(email));
            dispatch(setPassword(password));
            dispatch(setIsLoginLoading(false));
          });

          return '2fa';
        }

        await tryUpdatePushToken();

        const convertedUser = convertLegacyAccountInfoToCore(legacyLoginResponse);
        batch(() => {
          dispatch(setUser(convertedUser));
          dispatch(setIsLoginLoading(false));
        });
      } catch (error) {
        const message = extractErrorMessageFromResponse(error);
        batch(() => {
          dispatch(setIsLoginLoading(false));
          dispatch(setIsLoginError(`${message ? message : error}`));
        });
      }
    };

/**
 * @description Will try to retrive the user id from: state, storage, (legacy backend only) request account/info/app
 * @error On all failed attemts, will throw 'NoUserIdError'
 */
const ensureUserId = (): AppThunk<Promise<number>> => async (_, getState) => {
  const stateId = selectUserId(getState());
  if (stateId) return stateId;

  if (!shouldUseCoreApi()) {
    const accountInfo = await getLegacyAccountInfo();
    if (!('error' in accountInfo)) return accountInfo.id;
  }

  throw new NoUserIdError();
};

export const refreshUser =
  (disableLoading = false): AppThunk =>
  async (dispatch, getState: () => RootState) => {
    if (!disableLoading) dispatch(setIsLoginLoading(true));
    try {
      const recheckTimeout = getItem('recheckTimeOut');
      if (recheckTimeout) {
        clearTimeout(recheckTimeout);
        removeItem('recheckTimeOut');
      }

      const userId = await dispatch(ensureUserId());

      let tenantId = selectTenantId(getState());
      if (!tenantId) {
        await dispatch(fetchTenant());
        tenantId = selectTenantId(getState());
        if (!tenantId) {
          throw new MissingTenantError();
        }
      }

      const userResponse = await getUserById({ userId, tenantId });

      if (userResponse.error) {
        throw new Error('Cannot fetch nor rehydrate user');
      }

      batch(() => {
        dispatch(setUser(userResponse.data.data));
        dispatch(setIsLoginLoading(false));
      });
    } catch (error) {
      if (!(error instanceof NoUserIdError)) {
        console.error('error', error);
      }
      if (error instanceof MissingTenantError) {
        console.error('Missing tenant when refreshing user');
      }

      batch(() => {
        dispatch(getState().user?.user ? setIsLoginError(`${error}`) : setIsLoginError(null));
        dispatch(setIsLoginLoading(false));
      });
    }
  };

export const refreshUserPermissions = (): AppThunk => async (dispatch, getState) => {
  try {
    const userId = await dispatch(ensureUserId());

    let tenantId = selectTenantId(getState());
    if (!tenantId) {
      await dispatch(fetchTenant());
      tenantId = selectTenantId(getState());
      if (!tenantId) {
        throw new MissingTenantError();
      }
    }

    const userPermissions = await getUserPermissions({ userId, tenantId });

    if (userPermissions.error) {
      throw new Error('Could not fetch user permissions');
    }

    dispatch(setUserPermissions(userPermissions.data.data));
  } catch (error) {
    console.log('error log ', error);
  }
};

export const ssoLogin =
  (props: SsoLoginProps): AppThunk =>
  async dispatch => {
    const { email, code, provider } = props;
    try {
      dispatch(setIsLoginLoading(true));
      const legacyAccountInfo = await ssoLoginAPI({
        email,
        code,
        url: getSsoRedirectUrl(email, provider),
        provider,
      });

      await tryUpdatePushToken();

      batch(() => {
        dispatch(setUser(convertLegacyAccountInfoToCore(legacyAccountInfo)));
        dispatch(setIsLoginLoading(false));
      });
    } catch (error) {
      console.log('error log ', error);
      batch(() => {
        dispatch(setIsLoginLoading(false));
        dispatch(setIsLoginError(`${error}`));
      });
    }
  };

export const getSsoUrl =
  (email: string | null, provider?: string): AppThunk =>
  async dispatch => {
    dispatch(setIsLoginLoading(true));
    try {
      let redirect = getSsoRedirectUrl(email, provider);
      if (email) {
        email = encodeURIComponent(email);
      }
      redirect = encodeURIComponent(redirect);

      let url = 'AuthorizationUrl';

      if (email) {
        url += '?email=' + email;
      } else {
        url += '?provider=' + provider;
      }

      url += '&redirectUrl=' + redirect;

      const res = await fetchSsoUrl(url);
      if (isElectron()) {
        window.open(res.url);
      } else {
        window.location.href = res.url;
      }
    } catch (error) {
      console.error('getSsoUrl.error', error);
      batch(() => {
        dispatch(setIsLoginLoading(false));
        dispatch(setIsLoginError(`${error}`));
      });
    }
  };

export const loginUsingSsoCode =
  (email: string, code: string): AppThunk =>
  async dispatch => {
    try {
      dispatch(setIsLoginLoading(true));
      const legacyAccountInfo = await ssoLoginAPI({
        email: email,
        code: code,
        url: getSsoRedirectUrl(email),
      });

      await tryUpdatePushToken();

      batch(() => {
        dispatch(setUser(convertLegacyAccountInfoToCore(legacyAccountInfo)));
        dispatch(setIsLoginLoading(false));
      });
    } catch (error) {
      console.error('loginUsingSsoCode.error', error);
      batch(() => {
        dispatch(setIsLoginLoading(false));
        dispatch(setIsLoginError(`${error}`));
      });
    }
  };

export const editSecondaryPhoneNumber =
  (dialCode: string, secondaryPhoneNumber: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const user = selectUser(getState());
    if (!user) {
      await dispatch(refreshUser());
      dispatch(editSecondaryPhoneNumber(dialCode, secondaryPhoneNumber));
      return;
    }
    const tenant = selectTenant(getState());
    if (!tenant) {
      await dispatch(fetchTenant());
      dispatch(editSecondaryPhoneNumber(dialCode, secondaryPhoneNumber));
      return;
    }

    try {
      await updateUser({
        userId: user.id,
        tenantId: tenant.id,
        body: {
          phoneNumbers: {
            secondary: secondaryPhoneNumber?.length
              ? `${dialCode?.trim()}${secondaryPhoneNumber?.trim()}`
              : null,
          },
        },
        primaryPhoneNumber: user.phoneNumbers?.primary ?? '',
      });
      const userRes = await getUserById({ userId: user.id, tenantId: tenant.id });
      const updatedUser = userRes?.data?.data;
      if (!updatedUser) {
        throw new Error('Cannot fetch the updated user');
      }
      batch(() => {
        dispatch(setUser(updatedUser));
      });
    } catch (error) {
      batch(() => {
        dispatch(setIsLoginError(`${error}`));
      });
    }
  };

export const setIsVerifyAction =
  (res: boolean): AppThunk =>
  async dispatch => {
    try {
      dispatch(setIsLoginLoading(true));
      batch(() => {
        dispatch(setIsUserVerified(res));
        dispatch(setIsLoginLoading(false));
      });
    } catch (error) {
      batch(() => {
        dispatch(setIsLoginLoading(false));
        dispatch(setIsLoginError(`${error}`));
      });
    }
  };

export const changeProfileImage =
  (photoFileName: string | null): AppThunk =>
  async (dispatch, getState) => {
    const user = selectUser(getState());
    if (!user) {
      await dispatch(refreshUser());
      dispatch(changeProfileImage(photoFileName));
      return;
    }

    try {
      const legacyUser = await updateProfileImage({
        photoFileName,
      });
      const updatedUser = convertLegacyUserByIdToCore(legacyUser);
      dispatch(setUser(updatedUser));
    } catch (error) {
      batch(() => {
        dispatch(setIsLoginError(`${error}`));
      });
    }
  };
